// /onboarding/handover — «متى يستلم الزبون ويسلّم؟» (board 22
// enroll/handover--shape-4, verbatim): shape 4's step 5. «وقت الدخول»
// (default ٣:٠٠ م) and «وقت الخروج» (default ١٢:٠٠ م) apply to every unit
// type unless a type overrides them. «أيام مغلقة بشكل دائم» has no backend
// and is not drawn (R9/G6). The submit writes the business with the times on
// the scheduling PUT (check_in_min / check_out_min) — NO hours (R16). Booking
// validates the pair; its 422 row lands under its own field.
// States: default · invalid · saving (· error cards through the shell).

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PrimaryAction } from '@/components/Button';
import { sessionStore } from '@/lib/auth';
import { useLayout } from '@/lib/layout';
import { Msg } from '../components/widgets';
import { Wizard } from '../components/Wizard';
import { HANDOVER, HOURS, SHELL } from '../copy';
import { afterWriting } from '../data/enroll';
import { ONB } from '../data/flow';
import { onboardingStore, toMinutes, useOnboarding } from '../state';
import { useBusinessPreview } from './Business';
import { useEnrollRun } from './useEnrollRun';

type Field = 'in' | 'out';

export function HandoverScreen() {
  const mobile = useLayout() === 'phone';
  const { handover, draft, createdId } = useOnboarding();
  const { profile, preview } = useBusinessPreview('hours');
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  // An unwritten business (a failed write, then a reload) stays here to finish it.
  const [existedOnOpen] = useState(() => !!onboardingStore.get().createdId && onboardingStore.get().scheduled);
  const { phase, failure, run } = useEnrollRun(undefined, (field, message) => {
    if (field === 'check_in_min') setErrors((e) => ({ ...e, in: message }));
    else if (field === 'check_out_min') setErrors((e) => ({ ...e, out: message }));
    else return false;
    return true;
  });

  if (!sessionStore.isAuthenticated()) return <Navigate to={ONB.phone} replace />;
  // A written business never re-runs this step: on to the shape step until its minimum is met (R8), else done.
  if (createdId && existedOnOpen) return <Navigate to={afterWriting()} replace />;
  if (!draft.name.trim() || !draft.category || !draft.city.trim()) return <Navigate to={ONB.name} replace />;

  const submit = () => {
    if (phase === 'saving') return;
    const e: Partial<Record<Field, string>> = {};
    if (toMinutes(handover.checkIn) == null) e.in = HANDOVER.invalid;
    if (toMinutes(handover.checkOut) == null) e.out = HANDOVER.invalid;
    setErrors(e);
    if (e.in || e.out) return;
    void run();
  };

  const saving = phase === 'saving';
  const invalid = !!(errors.in || errors.out);
  const row = (field: Field, label: string, value: string, testId: string) => (
    <div className="onb-row onb-handover-row">
      <label className="onb-row-text onb-row-title" htmlFor={testId}>
        {label}
      </label>
      <input
        id={testId}
        type="time"
        className="onb-time ltr"
        dir="ltr"
        step={900}
        value={value}
        aria-invalid={errors[field] ? true : undefined}
        data-testid={testId}
        onChange={(ev) => {
          setErrors((x) => ({ ...x, [field]: undefined }));
          onboardingStore.setHandover(field === 'in' ? { checkIn: ev.target.value } : { checkOut: ev.target.value });
        }}
      />
    </div>
  );

  return (
    <Wizard
      screen="handover"
      testId="onb-handover"
      state={saving ? 'saving' : invalid ? 'invalid' : 'default'}
      list
      question={HANDOVER.title(profile)}
      sub={HANDOVER.sub}
      primary={
        <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile} loading={saving}>
          {saving ? HOURS.saving : SHELL.continue}
        </PrimaryAction>
      }
      onSubmit={submit}
      error={failure ? { ...failure, onRetry: () => void run() } : null}
      panel={{ phase: 'preview', profile, preview }}
    >
      <div className="onb-listcard" data-testid="onb-handover-times">
        {row('in', HANDOVER.checkIn, handover.checkIn, 'onb-check-in')}
        {errors.in && <Msg testId="onb-check-in-error">{errors.in}</Msg>}
        {row('out', HANDOVER.checkOut, handover.checkOut, 'onb-check-out')}
        {errors.out && <Msg testId="onb-check-out-error">{errors.out}</Msg>}
      </div>
      <Msg tone="info" testId="onb-handover-note">
        {HANDOVER.note}
      </Msg>
    </Wizard>
  );
}
